import React, { useState, useEffect } from 'react';
import { HelpCircle, MessageSquare, ThumbsUp, ThumbsDown, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function QA() {
  const { user } = useAuth();
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newQuestion, setNewQuestion] = useState('');
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [answers, setAnswers] = useState({});
  const [newAnswer, setNewAnswer] = useState({});

  useEffect(() => { loadQueries(); }, []);

  const loadQueries = async () => {
    try {
      const res = await api.get('/qa/queries?limit=30');
      setQueries(res.data.queries || []);
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const handleAsk = async () => {
    if (!newQuestion.trim()) return;
    setPosting(true);
    try {
      const res = await api.post('/qa/queries', { question: newQuestion });
      setQueries([res.data, ...queries]);
      setNewQuestion('');
    } catch (err) { console.error(err) }
    setPosting(false);
  };

  const toggleExpand = async (queryId) => {
    if (expanded[queryId]) {
      setExpanded({ ...expanded, [queryId]: false });
      return;
    }
    try {
      const res = await api.get(`/qa/queries/${queryId}`);
      setAnswers({ ...answers, [queryId]: res.data.answers || [] });
      setExpanded({ ...expanded, [queryId]: true });
    } catch (err) { console.error(err) }
  };

  const handleAnswer = async (queryId) => {
    const text = newAnswer[queryId];
    if (!text?.trim()) return;
    try {
      const res = await api.post(`/qa/queries/${queryId}/answers`, { content: text });
      setAnswers({
        ...answers,
        [queryId]: [...(answers[queryId] || []), res.data]
      });
      setNewAnswer({ ...newAnswer, [queryId]: '' });
      setQueries(queries.map(q => q.query_id === queryId ? { ...q, answer_count: (q.answer_count || 0) + 1 } : q));
    } catch (err) { console.error(err) }
  };

  const handleVote = async (answerId, vote) => {
    try {
      const res = await api.post(`/qa/answers/${answerId}/vote`, { vote });
      // Update answer votes locally
      for (const qId of Object.keys(answers)) {
        setAnswers(prev => ({
          ...prev,
          [qId]: (prev[qId] || []).map(a => a.answer_id === answerId ? { ...a, votes: res.data.votes } : a)
        }));
      }
    } catch (err) { console.error(err) }
  };

  const handleAccept = async (answerId) => {
    try {
      await api.post(`/qa/answers/${answerId}/accept`);
      for (const qId of Object.keys(answers)) {
        setAnswers(prev => ({
          ...prev,
          [qId]: (prev[qId] || []).map(a => ({ ...a, is_accepted: a.answer_id === answerId }))
        }));
      }
    } catch (err) { console.error(err) }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-safe">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="qa-title">
        Questions & Answers
      </h1>

      {/* Ask Question */}
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 mb-6" data-testid="ask-question-form">
        <div className="flex items-start gap-3">
          <HelpCircle className="w-5 h-5 text-[#4CAF50] mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <textarea
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="Ask a question about Lamnso'..."
              className="w-full border-0 resize-none text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none min-h-[60px]"
              data-testid="question-input"
            />
            <div className="flex justify-end">
              <Button onClick={handleAsk} disabled={posting || !newQuestion.trim()}
                className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-sm"
                data-testid="submit-question-button"
              >
                {posting ? '...' : 'Ask'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Questions List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" />
        </div>
      ) : (
        <div className="space-y-3" data-testid="queries-list">
          {queries.map((q) => (
            <div key={q.query_id} className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden" data-testid={`query-${q.query_id}`}>
              <button onClick={() => toggleExpand(q.query_id)}
                className="w-full p-5 text-left hover:bg-[#FAFAF9] transition-colors"
                data-testid={`expand-query-${q.query_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#1C1917]">{q.question}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-[#A8A29E]">
                      <span>{q.author?.name || 'Anonymous'}</span>
                      {q.author?.level >= 4 && <span className="text-xs font-bold bg-[#D4A373] text-white px-1.5 py-0.5 rounded">Wirfon</span>}
                      <span>{formatTime(q.created_at)}</span>
                      <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{q.answer_count || 0}</span>
                      {q.resolved && <span className="text-[#588157] font-semibold flex items-center gap-0.5"><Check className="w-3 h-3" />Resolved</span>}
                    </div>
                  </div>
                  {expanded[q.query_id] ? <ChevronUp className="w-4 h-4 text-[#A8A29E]" /> : <ChevronDown className="w-4 h-4 text-[#A8A29E]" />}
                </div>
              </button>

              {expanded[q.query_id] && (
                <div className="border-t border-[#E7E5E4] p-5 space-y-3">
                  {(answers[q.query_id] || []).map((a) => (
                    <div key={a.answer_id} className={`rounded-xl p-4 ${a.is_accepted ? 'bg-[#EAF1EA] border border-[#588157]/30' : 'bg-[#F5F5F4]'}`}
                      data-testid={`answer-${a.answer_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center gap-1">
                          <button onClick={() => handleVote(a.answer_id, 1)} className="text-[#A8A29E] hover:text-[#588157]" data-testid={`upvote-${a.answer_id}`}>
                            <ThumbsUp className="w-4 h-4" />
                          </button>
                          <span className="text-xs font-bold text-[#1C1917]">{a.votes || 0}</span>
                          <button onClick={() => handleVote(a.answer_id, -1)} className="text-[#A8A29E] hover:text-[#4CAF50]" data-testid={`downvote-${a.answer_id}`}>
                            <ThumbsDown className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-[#1C1917]">{a.content}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-[#A8A29E]">
                            <span>{a.author?.name}</span>
                            {a.author?.level >= 4 && <span className="text-xs font-bold bg-[#D4A373] text-white px-1.5 py-0.5 rounded">Wirfon</span>}
                            {a.is_accepted && <span className="text-[#588157] font-semibold flex items-center gap-0.5"><Check className="w-3 h-3" />Accepted</span>}
                            {q.user_id === user?.user_id && !a.is_accepted && (
                              <button onClick={() => handleAccept(a.answer_id)}
                                className="text-[#588157] font-semibold hover:underline" data-testid={`accept-${a.answer_id}`}
                              >
                                Accept
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Write Answer */}
                  <div className="flex gap-2 pt-2">
                    <input
                      value={newAnswer[q.query_id] || ''}
                      onChange={(e) => setNewAnswer({ ...newAnswer, [q.query_id]: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && handleAnswer(q.query_id)}
                      placeholder="Write your answer..."
                      className="flex-1 rounded-xl border-0 py-2.5 px-3 text-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
                      data-testid={`answer-input-${q.query_id}`}
                    />
                    <Button onClick={() => handleAnswer(q.query_id)} size="sm"
                      className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl"
                      data-testid={`submit-answer-${q.query_id}`}
                    >
                      Answer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {queries.length === 0 && (
            <p className="text-center text-sm text-[#A8A29E] py-8">No questions yet. Ask the first one!</p>
          )}
        </div>
      )}
    </div>
  );
}
